import { Component, EventEmitter, Input, Output } from "@angular/core";

import { OcrService } from "../../../services/ocr/ocr.service";
import { OcrRecord } from "../../../models/ocr.model";

/**
 * Upload the front and back of an identity document, read it, and hand the
 * result to the host form.
 *
 * This component does not decide what to do with the values - each host form
 * fills its own fields and looks up its own lead. It owns the upload, the wait,
 * and telling the operator when the scan cannot be relied upon.
 *
 * Ported from the VIE frontend. Differences forced by Angular 5:
 *   - native file inputs rather than the dropzone components that project has;
 *   - no `standalone` metadata, no optional chaining (TypeScript 3.2);
 *   - `scanning` is cleared in both callbacks instead of with `finalize`.
 */
@Component({
  selector: "app-id-document-scan",
  templateUrl: "./id-document-scan.component.html",
  styleUrls: ["./id-document-scan.component.css"]
})
export class IdDocumentScanComponent {
  @Input() heading = "Scan national ID";
  @Input() disabled = false;

  /** Emitted once the document has been read successfully. */
  @Output() scanned = new EventEmitter<OcrRecord>();
  /** Emitted when the operator clears the scan, so the host can undo its fill. */
  @Output() cleared = new EventEmitter<void>();
  /**
   * The images that were read, for hosts that also store ID photographs and
   * should not make the operator upload the same two files twice.
   */
  @Output() filesUsed = new EventEmitter<any>();

  frontFile: File = null;
  backFile: File = null;
  frontPreview: string = null;
  backPreview: string = null;

  scanning = false;
  result: OcrRecord = null;
  criticalMessage: string = null;
  errorMessage: string = null;
  uncertainCount = 0;

  constructor(private ocrService: OcrService) {}

  get canScan(): boolean {
    return !!this.frontFile && !this.scanning && !this.disabled;
  }

  onFileChosen(event: any, side: string): void {
    const files = event && event.target ? event.target.files : null;
    if (!files || !files.length) {
      return;
    }
    const file = files[0];

    if (side === "front") {
      this.frontFile = file;
      this.readPreview(file, "front");
    } else {
      this.backFile = file;
      this.readPreview(file, "back");
    }

    // A new image invalidates whatever the previous scan concluded.
    this.result = null;
    this.criticalMessage = null;
    this.errorMessage = null;
    this.uncertainCount = 0;
  }

  removeFile(side: string): void {
    if (side === "front") {
      this.frontFile = null;
      this.frontPreview = null;
    } else {
      this.backFile = null;
      this.backPreview = null;
    }
    this.result = null;
    this.criticalMessage = null;
  }

  scan(): void {
    if (!this.canScan) {
      return;
    }

    const files: File[] = [];
    if (this.frontFile) {
      files.push(this.frontFile);
    }
    if (this.backFile) {
      files.push(this.backFile);
    }

    this.scanning = true;
    this.errorMessage = null;
    this.criticalMessage = null;
    this.result = null;

    this.ocrService.scanDocuments(files).subscribe(
      (record: OcrRecord) => {
        this.scanning = false;

        if (!record) {
          this.errorMessage =
            "The images were processed but no identity record could be read. Try clearer photographs of the card.";
          return;
        }

        this.result = record;
        this.uncertainCount = this.countUncertain(record);

        const critical = this.ocrService.criticalFlags(record);
        if (critical.length) {
          this.criticalMessage = critical[0];
        }

        this.scanned.emit(record);
        this.filesUsed.emit({ front: this.frontFile, back: this.backFile });
      },
      (error: any) => {
        this.scanning = false;
        this.errorMessage = this.describeError(error);
      }
    );
  }

  reset(): void {
    this.frontFile = null;
    this.backFile = null;
    this.frontPreview = null;
    this.backPreview = null;
    this.result = null;
    this.criticalMessage = null;
    this.errorMessage = null;
    this.uncertainCount = 0;
    this.cleared.emit();
  }

  private readPreview(file: File, side: string): void {
    const reader = new FileReader();
    reader.onload = () => {
      if (side === "front") {
        this.frontPreview = reader.result as string;
      } else {
        this.backPreview = reader.result as string;
      }
    };
    reader.readAsDataURL(file);
  }

  private countUncertain(record: OcrRecord): number {
    const identity = record && record.identity ? record.identity : {};
    const keys = Object.keys(identity);
    let count = 0;
    for (let i = 0; i < keys.length; i++) {
      if (this.ocrService.needsVerification(record, keys[i])) {
        count++;
      }
    }
    return count;
  }

  /**
   * The CRM proxy returns specific statuses worth explaining rather than
   * showing a generic failure - 502 and 503 are operational and not the
   * operator's fault, while 422 usually means the photograph needs retaking.
   */
  private describeError(error: any): string {
    const status = error && error.status ? error.status : 0;
    const fromApi =
      error && error.error && error.error.message ? error.error.message : null;

    if (status === 422) {
      return fromApi || "Those images could not be read. Try clearer photographs.";
    }
    if (status === 502) {
      return "The OCR service is currently unavailable. Enter the details manually and try scanning later.";
    }
    if (status === 503) {
      return "The OCR service is not configured. Ask an administrator to check it.";
    }
    if (status === 413) {
      return "Those images are too large. Each must be under 10 MB.";
    }
    if (status === 401) {
      return "Not authorised to use the OCR service. Ask an administrator to check the API key.";
    }
    if (!status) {
      return "Could not reach the server. Check your connection and try again.";
    }
    return fromApi || "Could not read the document. Enter the details manually.";
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/components/id-document-scan/id-document-scan.component.ts